import React from 'react'
import { Redirect, useLocation, useParams } from 'react-router'
import { parse, stringify } from 'query-string'
import { useGetArtboardByPermanentArtboardShortIdQuery } from '@sketch/gql-types'
import { LocationDescriptorObject } from 'history'
import {
  useFlag,
  DynamicLoadingPage,
  RouteParams,
  NotFoundView,
} from '@sketch/modules-common'
import { useVersioning } from 'modules/versioning'

/**
 * Redirects from the legacy PROTOTYPE route to the PROTOTYPE_VIEW route.
 */
export function PrototypeArtboardShortIdToUUIDRedirect() {
  const { permanentArtboardShortId } = useParams<RouteParams<'PROTOTYPE'>>()
  const { getPathname, versionShortId, share } = useVersioning()
  const { search } = useLocation()
  const isBetterPrototypeNavBarOn = useFlag('better-prototype-nav-bar')
  const isWebGLPrototypePlayerOn = useFlag('webgl-prototype-player')
  const shouldAttachPrototypeIdToUrl =
    !isWebGLPrototypePlayerOn && isBetterPrototypeNavBarOn

  const { data, loading } = useGetArtboardByPermanentArtboardShortIdQuery({
    variables: {
      documentVersionShortId: versionShortId,
      permanentArtboardShortId,
      shareIdentifier: share.identifier,
    },
  })

  const uuid = data?.artboard?.uuid

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (!uuid) {
    return <NotFoundView />
  }

  const isFlowHome = Boolean(data?.artboard?.isFlowHome)

  const to: LocationDescriptorObject = isWebGLPrototypePlayerOn
    ? {
        pathname: getPathname({
          routeKey: 'PROTOTYPE_VIEW_NEXT',
          routeParams: {
            shareID: share.identifier,
            prototypeArtboardUUID: uuid,
            currentArtboardUUID: uuid,
          },
        }),
      }
    : {
        pathname: getPathname({
          routeKey: 'PROTOTYPE_VIEW',
          routeParams: {
            shareID: share.identifier,
            artboardUUID: uuid,
          },
        }),
        search:
          shouldAttachPrototypeIdToUrl && isFlowHome
            ? addPrototypeQueryParamsToUrlSearchString(search, uuid)
            : search,
      }

  return <Redirect to={to} push={false} />
}

function addPrototypeQueryParamsToUrlSearchString(
  currentSearchString: string,
  prototypeId: string
) {
  const queryParams = parse(currentSearchString) as Record<string, string>
  queryParams.prototype = prototypeId

  return stringify(queryParams)
}
